import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useNavigationActions } from '../hooks/useNavigationActions';

const MobileNavigation = ({ isLegalPage = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef(null);
  const { scrollToSection, navigateToSection } = useNavigationActions();

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 0;
      setIsScrolled(scrolled);
      
      // Close menu when scrolling if menu is open
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  // Handle clicks outside of menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target) && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);


  return (
    <motion.nav
      ref={navRef}
      className={`md:hidden fixed top-6 left-3 right-3 z-50 transition-all duration-500 ${
        (isScrolled || isOpen)
          ? 'backdrop-blur-lg border border-white/10' 
          : 'border border-transparent'
      }`}
      style={{
        backgroundColor: (isScrolled || isOpen) ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
        borderRadius: '30px',
        boxShadow: (isScrolled || isOpen)
          ? 'rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset' 
          : 'none'
      }}
      layout
      transition={{
        layout: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }
      }}
    >
      {/* Header Row - Always Visible */}
      <div className="flex items-center justify-between p-4">
        {/* Antix Wordmark */}
        <div className="pl-2">
          {isLegalPage ? (
            <Link to="/" aria-label="Return to Antix homepage">
              <h1 className="text-2xl font-semibold text-white cursor-pointer font-['Space_Grotesk']">
                Antix
              </h1>
            </Link>
          ) : (
            <h1
              className="text-2xl font-semibold text-white cursor-pointer font-['Space_Grotesk']"
              onClick={() => {
                // Track home/logo click
                if (window.gtag) {
                  window.gtag('event', 'navigation_click', {
                    event_category: 'Navigation',
                    event_label: 'home',
                  });
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsOpen(false);
              }}
            >
              Antix
            </h1>
          )}
        </div>

        {/* Menu Button */}
        <motion.button
          className="bg-white text-black px-4 py-2 rounded-full font-semibold text-sm"
          onClick={() => setIsOpen(!isOpen)}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
        >
          {isOpen ? 'Close' : 'Menu'}
        </motion.button>
      </div>

      {/* Expandable Navigation Links */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ 
              duration: 0.4, 
              ease: [0.25, 0.1, 0.25, 1],
              opacity: { duration: 0.3 }
            }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4">
              {/* Navigation Links with left padding */}
              <div className="pl-2 space-y-4">
                {isLegalPage ? (
                  <Link 
                    to="/" 
                    onClick={() => setIsOpen(false)}
                    className="block"
                  >
                    <motion.div
                      className="w-full text-left text-white text-xl font-semibold py-2 hover:text-white/80 transition-colors duration-200 font-['Inter']"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                    >
                      Home
                    </motion.div>
                  </Link>
                ) : (
                  <motion.button
                    onClick={() => {
                      setIsOpen(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="block w-full text-left text-white text-xl font-semibold py-2 hover:text-white/80 transition-colors duration-200 font-['Inter']"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    Home
                  </motion.button>
                )}

                <motion.button
                  onClick={() => {
                    // Track navigation click
                    if (window.gtag) {
                      window.gtag('event', 'navigation_click', {
                        event_category: 'Navigation',
                        event_label: 'about',
                      });
                    }
                    setIsOpen(false);
                    if (isLegalPage) {
                      navigateToSection('about');
                    } else {
                      setTimeout(() => scrollToSection('about'), 300);
                    }
                  }}
                  className="block w-full text-left text-white text-xl font-semibold py-2 hover:text-white/80 transition-colors duration-200 font-['Inter']"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                >
                  About
                </motion.button>

                <motion.button
                  onClick={() => {
                    // Track navigation click
                    if (window.gtag) {
                      window.gtag('event', 'navigation_click', {
                        event_category: 'Navigation',
                        event_label: 'features',
                      });
                    }
                    setIsOpen(false);
                    if (isLegalPage) {
                      navigateToSection('features');
                    } else {
                      setTimeout(() => scrollToSection('features'), 300);
                    }
                  }}
                  className="block w-full text-left text-white text-xl font-semibold py-2 hover:text-white/80 transition-colors duration-200 font-['Inter']"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  Features
                </motion.button>

                <motion.button
                  onClick={() => {
                    // Track navigation click
                    if (window.gtag) {
                      window.gtag('event', 'navigation_click', {
                        event_category: 'Navigation',
                        event_label: 'mission',
                      });
                    }
                    setIsOpen(false);
                    if (isLegalPage) {
                      navigateToSection('mission');
                    } else {
                      setTimeout(() => scrollToSection('mission'), 300);
                    }
                  }}
                  className="block w-full text-left text-white text-xl font-semibold py-2 hover:text-white/80 transition-colors duration-200 font-['Inter']"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25, duration: 0.3 }}
                >
                  Mission
                </motion.button>
              </div>

              {/* Separator Line - no left padding */}
              <motion.div
                className="border-t border-white/20 my-4"
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              />

              {/* Social Media Icons - no left padding */}
              <motion.div
                className="flex items-center justify-center space-x-8 py-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.3 }}
              >
                {/* Instagram */}
                <motion.a
                  href="https://www.instagram.com/antix.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200"
                  whileTap={{ scale: 0.95 }}
                  aria-label="Instagram"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'social_click', {
                        event_category: 'Social',
                        event_label: 'instagram_nav_mobile',
                      });
                    }
                  }}
                >
                  <Instagram size={24} className="text-white" />
                </motion.a>

                {/* TikTok */}
                <motion.a
                  href="#"
                  className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200"
                  whileTap={{ scale: 0.95 }}
                  aria-label="TikTok"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'social_click', {
                        event_category: 'Social',
                        event_label: 'tiktok_nav_mobile',
                      });
                    }
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="text-white"
                  >
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </motion.a>

                {/* LinkedIn */}
                <motion.a
                  href="#"
                  className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200"
                  whileTap={{ scale: 0.95 }}
                  aria-label="LinkedIn"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'social_click', {
                        event_category: 'Social',
                        event_label: 'linkedin_nav_mobile',
                      });
                    }
                  }}
                >
                  <Linkedin size={24} className="text-white" />
                </motion.a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

MobileNavigation.propTypes = {
  isLegalPage: PropTypes.bool
};

MobileNavigation.defaultProps = {
  isLegalPage: false
};

export default MobileNavigation;